import { HelseRegion } from '../types/ExtendEnums';
import type {
  BehandlingsstederBehandlingssted,
  Behandlingstilbud,
  Adresse,
} from '../types/Helsenorge.VelgBehandlingssted.Libraries.EntitiesEntities';
import type { HNVelgBehandlingsstedFrontend } from '../types/Resources';

import { sortFilterValues } from '../utils/filters';
import { hentHelseregionString } from '../utils/strings';

const filterValueOffentlige = 'offentlige';
const filterValuePrivate = 'private';

export const adresseHasSearchString = (adresse: Adresse | null, searchText: string): boolean => {
  return adresse
    ? (!!adresse.gateadresse && adresse.gateadresse.toLowerCase().includes(searchText)) ||
        (!!adresse.postnr && adresse.postnr.includes(searchText)) ||
        (!!adresse.poststed && adresse.poststed.toLowerCase().includes(searchText))
    : false;
};

export type FilterObject = { name?: string; id?: string; values: Array<FilterValue>; index: number };
export type FilterValue = { checked?: boolean; id?: string; value: string; displayName?: string };
export type BehandlingsstederFilters = {
  helseregion: string[];
  behandlingstilbud: string[];
  behandlingssted: string[];
  fritekst: string;
};

export const getFilteredBehandlingssteder = (
  behandlingssteder: Array<BehandlingsstederBehandlingssted>,
  filters: Partial<BehandlingsstederFilters>
): Array<BehandlingsstederBehandlingssted> => {
  const helseregionCheckedFilters = filters.helseregion ?? [];
  const behandlingstilbudCheckedFilters = filters.behandlingstilbud ?? [];
  const behandlingsstedCheckedFilters = filters.behandlingssted ?? [];
  const offentligeFilterChecked = behandlingsstedCheckedFilters.some(filter => filter === filterValueOffentlige);
  const privateFilterChecked = behandlingsstedCheckedFilters.some(filter => filter === filterValuePrivate);

  if (
    !helseregionCheckedFilters.length &&
    !behandlingstilbudCheckedFilters.length &&
    !filters.fritekst &&
    !offentligeFilterChecked &&
    !privateFilterChecked
  ) {
    return behandlingssteder;
  }

  const lowerCaseSearchText = filters.fritekst?.toLowerCase() ?? '';

  return behandlingssteder.filter(
    behandlingssted =>
      matchesSearchText(behandlingssted, lowerCaseSearchText) &&
      matchesHelseregion(behandlingssted, helseregionCheckedFilters) &&
      matchesBehandlingstilbud(behandlingssted, behandlingstilbudCheckedFilters) &&
      matchesOffentligPrivat(behandlingssted, offentligeFilterChecked, privateFilterChecked)
  );
};

const matchesSearchText = (behandlingssted: BehandlingsstederBehandlingssted, searchText: string): boolean => {
  if (!searchText) {
    return true;
  }

  if (behandlingssted.navn?.toLowerCase().includes(searchText)) {
    return true;
  }

  if (behandlingssted.navnBedriftsregister?.toLowerCase().includes(searchText)) {
    return true;
  }

  if (behandlingssted.orgnr?.toString().includes(searchText)) {
    return true;
  }

  if (behandlingssted.behandlingstilbud?.some(t => t.navn?.toLowerCase().includes(searchText))) {
    return true;
  }

  if (adresseHasSearchString(behandlingssted.besoksAdresse, searchText)) {
    return true;
  }

  if (adresseHasSearchString(behandlingssted.postAdresse, searchText)) {
    return true;
  }

  return false;
};

const matchesHelseregion = (
  behandlingssted: BehandlingsstederBehandlingssted,
  helseregionCheckedFilters: (string | undefined)[]
): boolean => {
  if (!helseregionCheckedFilters.length) return true;
  return behandlingssted.helseRegioner?.some(region => helseregionCheckedFilters.includes(region)) ?? false;
};

const matchesBehandlingstilbud = (
  behandlingssted: BehandlingsstederBehandlingssted,
  behandlingstilbudCheckedFilters: (string | undefined)[]
): boolean => {
  if (!behandlingstilbudCheckedFilters.length) return true;
  return behandlingssted.behandlingstilbud?.some(t => t.navn && behandlingstilbudCheckedFilters.includes(t.navn)) ?? false;
};

const matchesOffentligPrivat = (
  behandlingssted: BehandlingsstederBehandlingssted,
  offentligeFilterChecked: boolean,
  privateFilterChecked: boolean
): boolean => {
  if (offentligeFilterChecked && !privateFilterChecked) return !behandlingssted.erPrivat;
  if (privateFilterChecked && !offentligeFilterChecked) return behandlingssted.erPrivat;
  return true;
};

export const getInitialFilters = (
  resources: HNVelgBehandlingsstedFrontend,
  behandlingssteder: Array<BehandlingsstederBehandlingssted>
): Array<FilterObject> => {
  let helseregionFilterValues: Array<FilterValue> = [];
  let behandlingstilbudFilterValues: Array<FilterValue> = [];
  let behandlingsstederFilterValues: Array<FilterValue> = [];
  let filterOffentlige = false;
  let filterPrivate = false;

  behandlingssteder.forEach((behandlingssted: BehandlingsstederBehandlingssted, index: number) => {
    if (!!behandlingssted.helseRegioner && behandlingssted.helseRegioner.length) {
      for (const helseRegion of behandlingssted.helseRegioner) {
        if (
          !helseregionFilterValues.some(
            filterValue => helseRegion !== HelseRegion.Unknown && !!helseRegion && filterValue.value === helseRegion
          )
        ) {
          helseregionFilterValues = helseregionFilterValues.concat({
            checked: false,
            id: `helseregion-${helseRegion}`,
            displayName: hentHelseregionString(helseRegion, resources),
            value: helseRegion ? helseRegion : '',
          });
        }
      }
    }

    if (behandlingssted.behandlingstilbud) {
      behandlingssted.behandlingstilbud.forEach((tilbud: Behandlingstilbud, index2: number) => {
        if (!behandlingstilbudFilterValues.some(filterValue => filterValue.value === tilbud.navn)) {
          behandlingstilbudFilterValues = behandlingstilbudFilterValues.concat({
            checked: false,
            id: `behandlingstilbud${index}${index2}`,
            displayName: tilbud.navn ? tilbud.navn : '',
            value: tilbud.navn ? tilbud.navn : '',
          });
        }
      });
    }

    if (!filterOffentlige && !behandlingssted.erPrivat) {
      filterOffentlige = true;
      behandlingsstederFilterValues = behandlingsstederFilterValues.concat({
        checked: false,
        id: `behandlingssted-offentlige`,
        displayName: resources.filterBehandlingsstedOffentlige,
        value: filterValueOffentlige,
      });
    }
    if (!filterPrivate && behandlingssted.erPrivat) {
      filterPrivate = true;
      behandlingsstederFilterValues = behandlingsstederFilterValues.concat({
        checked: false,
        id: `behandlingssted-private`,
        displayName: resources.filterBehandlingsstedPrivate,
        value: filterValuePrivate,
      });
    }
  });

  return [
    {
      index: 0,
      values: sortFilterValues(helseregionFilterValues),
      name: resources.filterHelseregion,
    },
    {
      index: 1,
      values: sortFilterValues(behandlingstilbudFilterValues),
      name: resources.filterBehandlingstilbud,
    },
    {
      index: 2,
      values: sortFilterValues(behandlingsstederFilterValues),
      name: resources.filterBehandlingssted,
    },
  ];
};
